import React, { useContext, useState, useEffect, useRef, useCallback, memo } from "react";
import { LoggerContext } from "context/LoggerContext";
import styles from "./CardsSection.module.scss";
import SectionWrapper from "components/elements/SectionWrapper";
import Card from "components/elements/Card/Card";
import Loading from "components/Loading";
import CardsListSentinel from "../CardsListSection/CardsListSentinel";

/**
 * Card Item component with memoization to prevent unnecessary re-renders
 */
const CardItem = memo(({ item, index, layout, forceLoad }) => {
	return (
		<Card
			layout={layout || "standard"}
			title={item.title}
			lead={item.lead || item.short_desc}
			desc={item.desc}
			cta_text={item.cta_text}
			cta_link={item.cta_link}
			image={Array.isArray(item.media) ? item.media[0]?.mediaUrl : (item.image || item.thumbnail)}
			image_alt={item.image_alt || item.title}
			bg_image={item.bg_image}
			bg_image_alt={item.bg_image_alt}
			bg_color={item.bg_color}
			svg={item.svg}
			count_up={item.count_up}
			rating={item.rating}
			theme={item.theme}
			forceLoad={forceLoad}
			accent_color={item.accent_color}
			footer={item.footer}
		/>
	);
}, (prevProps, nextProps) => {
	// Deep comparison for memo
	return prevProps.item.id === nextProps.item.id && 
		prevProps.forceLoad === nextProps.forceLoad;
});

CardItem.displayName = "CardItem";

const CardsSection = (props) => {
	const { logError, logDebug } = useContext(LoggerContext);
	logError("propsprops", props);
	// Extract props with defaults
	const {
		data = [],
		loading: externalLoading = false,
		loadingMore: externalLoadingMore = false,
		pagination,
		layout = "standard",
		items_per_line = 3,
		tablet_items_per_line = 2,
		mobile_items_per_line = 1,
		items_per_page = 10,
		view, // For backward compatibility with different view types
		global_view = null, // Support for global_view configuration
		view_id = null //
	} = props;
	
	// Component state
	const [displayedItems, setDisplayedItems] = useState([]);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMoreItems, setHasMoreItems] = useState(true);
	const [isFirstRender, setIsFirstRender] = useState(true);
	const [initialDataLoaded, setInitialDataLoaded] = useState(false);
	
	// Tracking Refs
	const cardsContainerRef = useRef(null);
	const scrollYRef = useRef(0);
	const isLoadingRef = useRef(false); 
	const lastPageLoadedRef = useRef(pagination?.currentPage || 1);
	const loadLockRef = useRef(false);
	const loadLockTimeoutRef = useRef(null);
	const lastTriggerTimeRef = useRef(0);
	const processedItemsRef = useRef(new Set());
	const initialRenderTimerRef = useRef(null);
	const initialDataProcessedRef = useRef(false);
	const dataHasChangedRef = useRef(false);
	
	// Determine appropriate layout based on view configuration
	const determineLayout = () => {
		// If explicit layout is provided, use it
		if (layout && layout !== "standard") return layout;
		logError("Layout", view_id);
		// Otherwise, determine layout based on view
		if (view_id || view?.id || global_view?.id) {
			const viewId = view_id || view?.id || global_view?.id;
			console.log("layoutlayout", viewId);
			switch (viewId) {
				case 1: return "primary";
				case 2: return "secondary";
				case 3: return "horizontal";
				case 4: return "variant";
				case 5: return "feature";
				case 6: return "overlay";
				case 7: return "accent";
				case 8: return "testimonial";
				case 9: return "benefit-horizontal";
				default: return "standard";
			}
		}
		
		return layout || "standard";
	};
	
	const effectiveLayout = determineLayout();
	
	// Bootstrap column classes based on card layout
	const getColClasses = () => {
		// Special case for horizontal layout - always full width
		// if (effectiveLayout === "horizontal") {
		// 	return "col-12 mb-4";
		// }
		
		// Standard columns for other layouts
		return `col-${12 / mobile_items_per_line} col-md-${12 / tablet_items_per_line} col-lg-${12 / items_per_line} mb-4`;
	};
	
	const colClasses = getColClasses();
	
	// Track first data load
	useEffect(() => {
		if (isFirstRender && data && data.length > 0 && !externalLoading) {
			initialRenderTimerRef.current = setTimeout(() => {
				setIsFirstRender(false);
				logDebug("Initial render phase completed");
				
				// Mark that initial data has been loaded after a delay
				// to ensure DOM has been updated
				setTimeout(() => {
					setInitialDataLoaded(true);
					initialDataProcessedRef.current = true;
					logDebug("Initial data fully processed, sentinel can now be activated");
				}, 500);
			}, 500);
		}
		
		return () => {
			if (initialRenderTimerRef.current) {
				clearTimeout(initialRenderTimerRef.current);
			}
		};
	}, [data, externalLoading, isFirstRender, logDebug]);
	
	// Track pagination changes - CRITICAL FOR CORRECT PAGE LOADING
	useEffect(() => {
		// If pagination values change externally, update our tracking ref
		if (pagination?.currentPage) {
			// Only log when page actually changes
			if (lastPageLoadedRef.current !== pagination.currentPage) {
				logDebug("Pagination updated from parent", {
					previous: lastPageLoadedRef.current,
					current: pagination.currentPage
				});
				lastPageLoadedRef.current = pagination.currentPage;
			}
		}
	}, [pagination, logDebug]);
	
	// Process incoming data
	useEffect(() => {
		if (!data || !Array.isArray(data)) return;
		dataHasChangedRef.current = true;
		
		// Debugging: Log incoming data to check for cta_link and cta_text
		logDebug("CardsSection data received:", data.slice(0, 2).map(item => ({
			title: item.title,
			cta_text: item.cta_text,
			cta_link: item.cta_link
		})));
		
		const newItems = data.filter(item => {
			const itemId = item.id || item.cta_link || item.title;
			return !processedItemsRef.current.has(itemId);
		});
		newItems.forEach(item => {
			const itemId = item.id || item.cta_link || item.title;
			processedItemsRef.current.add(itemId);
		});
		if (newItems.length > 0) {
			if (!initialDataLoaded) {
				setDisplayedItems(newItems);
				setTimeout(() => {
					setInitialDataLoaded(true);
					initialDataProcessedRef.current = true;
					logDebug("Initial card data loaded");
				}, 200);
			} else {
				setDisplayedItems(prev => [...prev, ...newItems]);
			}
			if (pagination?.currentPage) {
				lastPageLoadedRef.current = pagination.currentPage;
				logDebug("Updated lastPageLoadedRef after new data", {
					page: lastPageLoadedRef.current,
					newItemsCount: newItems.length,
					hasMorePages: pagination.currentPage < pagination.totalPages
				});
				}
			const resetTimeout = setTimeout(() => {
				if (loadingMore) {
					window.scrollTo(0, scrollYRef.current);
				}
				setLoadingMore(false);
				isLoadingRef.current = false;
				if (loadLockTimeoutRef.current) {
					clearTimeout(loadLockTimeoutRef.current);
				}
				loadLockRef.current = false;
				logDebug("Loading states reset after data processed", {
					newItems: newItems.length,
					initialDataLoaded: initialDataLoaded,
					currentPage: lastPageLoadedRef.current
				});
			}, 150);
			return () => clearTimeout(resetTimeout);
		} else {
			// Do not automatically mark end-of-data on receiving no new items.
			setTimeout(() => {
				setLoadingMore(false);
				isLoadingRef.current = false;
				loadLockRef.current = false;
				// Removed condition marking end-of-data; pagination effect will handle it.
				if (!initialDataLoaded) {
					initialDataProcessedRef.current = true;
					setInitialDataLoaded(true);
					logDebug("Initial data marked as loaded (empty data set)");
				}
			}, 150);
		}
		logDebug("CardsSection data processed", {
			newItems: newItems.length,
			loadingMore: loadingMore,
			isLoading: isLoadingRef.current,
			loadLock: loadLockRef.current,
			hasMoreItems,
			pagination: pagination ? {
				currentPage: pagination.currentPage,
				totalPages: pagination.totalPages
			} : "none",
			layout: effectiveLayout
		});
		// Removed displayedItems.length and other volatile values from dependencies
	}, [data, loadingMore, pagination, logDebug, isFirstRender, initialDataLoaded, effectiveLayout, hasMoreItems]);
	
	// New effect to log and validate pagination.totalPages availability
	useEffect(() => {
		if (pagination) {
			if (pagination.totalPages === undefined || pagination.totalPages === null) {
				logDebug("Pagination totalPages is not available", { pagination });
			} else {
				logDebug("Pagination totalPages available", { totalPages: pagination.totalPages });
			}
		} else {
			logDebug("Pagination prop is not defined");
		}
	}, [pagination, logDebug]);
	
	// Update pagination status with improved reliability
	const prevHasMoreStateRef = useRef(hasMoreItems);
	
	useEffect(() => {
		// Skip updates during initial loading or if no pagination info
		if (isFirstRender || externalLoading || !pagination) return;

		// When pagination info is available
		const currentPage = pagination.currentPage || 1;
		const totalPages = pagination.totalPages || 1;
		
		// IMPROVED LOGIC: Don't just check page numbers, also check data characteristics
		let hasMore = currentPage < totalPages;
		
		// If API says no more pages but we're getting normal data flow, be skeptical
		if (!hasMore && data && data.length >= (items_per_page || 10)) {
			logDebug("API reports no more pages but full data set received - assuming more data exists");
			hasMore = true;
		}
		
		// If we've received less data than expected per page, likely at the end
		if (hasMore && data && data.length > 0 && data.length < (items_per_page || 10)) {
			logDebug("Partial page received, likely at end of data");
			hasMore = false;
		}
		
		// Only update if state would change or if data has changed
		if (prevHasMoreStateRef.current !== hasMore || dataHasChangedRef.current) {
			setHasMoreItems(hasMore);
			dataHasChangedRef.current = false;
			
			logDebug("Pagination status updated", {
				currentPage,
				totalPages,
				hasMore,
				previousState: prevHasMoreStateRef.current,
				lastPageLoaded: lastPageLoadedRef.current
			});
			
			// Update the ref to current value
			prevHasMoreStateRef.current = hasMore;
		}
	}, [pagination, externalLoading, logDebug, isFirstRender, data, items_per_page]);
	
	// Handle loading more items - CRITICAL FIX: Use lastPageLoadedRef for accurate tracking
	const handleLoadMore = useCallback(() => {
		if (
			isLoadingRef.current ||
			loadLockRef.current ||
			!pagination?.setCurrentPage ||
			externalLoading ||
			externalLoadingMore ||
			!hasMoreItems
		) {
			logDebug("Load more prevented:", {
				isLoading: isLoadingRef.current,
				loadLock: loadLockRef.current,
				hasSetCurrentPage: !!pagination?.setCurrentPage,
				externalLoading,
				externalLoadingMore,
				hasMoreItems,
				currentPage: pagination?.currentPage
			});
			return;
		}
		
		scrollYRef.current = window.scrollY;
		const now = Date.now();
		if (now - lastTriggerTimeRef.current < 500) return;
		lastTriggerTimeRef.current = now;
		
		setLoadingMore(true);
		isLoadingRef.current = true;
		loadLockRef.current = true;
		
		if (loadLockTimeoutRef.current) {
			clearTimeout(loadLockTimeoutRef.current);
		}
		loadLockTimeoutRef.current = setTimeout(() => {
			loadLockRef.current = false;
			isLoadingRef.current = false;
			setLoadingMore(false);
			logDebug("Auto-released locks after timeout");
		}, 8000);
		
		// Use pagination.currentPage for next page calculation
		const nextPage = (pagination.currentPage || 1) + 1;
		
		logDebug("Loading more cards...", {
			currentPage: pagination.currentPage,
			nextPage: nextPage,
			scrollY: scrollYRef.current
		});
		
		setTimeout(() => {
			pagination.setCurrentPage(nextPage);
		}, 10);
	}, [pagination, externalLoading, externalLoadingMore, hasMoreItems, logDebug]);
	
	// Clean up timeouts on unmount
	useEffect(() => {
		return () => {
			if (loadLockTimeoutRef.current) {
				clearTimeout(loadLockTimeoutRef.current);
			}
			if (initialRenderTimerRef.current) {
				clearTimeout(initialRenderTimerRef.current);
			}
		};
	}, []);
	
	// Determine whether to show the end message with more validation
	const showEndMessage = !hasMoreItems && !externalLoading && !isFirstRender && 
		displayedItems.length > 0;
	
	return (
		<SectionWrapper 
			{...props} 
			className={`${styles.CardsSection} ${loadingMore ? styles.loadingMore : ''} ${isFirstRender ? 'initialRender' : ''}`}
			data-initial-loaded={initialDataLoaded ? "true" : "false"}
			data-layout={effectiveLayout}
			data-page={lastPageLoadedRef.current}
			data-has-more={hasMoreItems ? "true" : "false"}>
			
			{/* Loading state for initial load */}
			{externalLoading && displayedItems.length === 0 ? (
				<div className="text-center py-4">
					<Loading />
				</div>
			) : (
				<div className="row" ref={cardsContainerRef}>
					{/* Card items */}
					{displayedItems.length > 0 ? (
						displayedItems.map((item, index) => (
							<div 
								key={item.id || item.cta_link || `item-${index}`}
								className={`${colClasses} ${item._isNew ? styles.newItem : ''}`}>
								<CardItem
									item={item}
									index={index}
									layout={effectiveLayout}
									forceLoad={false}
								/>
							</div>
						))
					) : !externalLoading && (
						<div className="col-12 text-center py-5">
							<p>Keine Einträge gefunden.</p>
						</div>
					)}
				</div>
			)}
			
			{/* Infinite Scroll Sentinel with improved debugging */}
			<CardsListSentinel
				onIntersect={handleLoadMore}
				hasMorePages={hasMoreItems || externalLoading || isFirstRender}
				isLoading={externalLoading}
				isLoadingMore={loadingMore || externalLoadingMore}
				initialDataLoaded={initialDataLoaded}
				loadMoreText="Weitere Einträge werden geladen..."
				endMessage="Sie haben das Ende der Liste erreicht."
				showEndMessage={showEndMessage}
			/>
		</SectionWrapper>
	);
};

export default CardsSection;